import { Grid } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DefaultStatisticsCard from "examples/Cards/StatisticsCards/DefaultStatisticsCard";
import { GetDashboardExtraKpisYtdQuery, GetYearToDateStatsQuery } from "generated/graphql";
import formatCentsToUSD from "utils/money/formatCentsToUSD";
import formatCentsToUSDNoDecimals from "utils/money/formatCentsToUSDNoDecimals";

function toNumber(value: unknown, fallback = 0) {
  if (value === null || value === undefined) return fallback;
  if (typeof value === "number") return Number.isFinite(value) ? value : fallback;
  if (typeof value === "string") {
    const n = Number(value);
    return Number.isFinite(n) ? n : fallback;
  }
  return fallback;
}

export default function YearToDateStats({
  yearToDateData,
  extraKpis,
  extraKpisLoading,
}: {
  yearToDateData: GetYearToDateStatsQuery["getYearToDateStats"];
  extraKpis?: GetDashboardExtraKpisYtdQuery["getDashboardExtraKpisYtd"] | null;
  extraKpisLoading?: boolean;
}) {
  const organizationSalesGoalCents = toNumber(extraKpis?.organizationSalesGoal, 0);
  const averageProposalAmountCents = toNumber(extraKpis?.averageProposalAmount, 0);
  const percentToGoal = toNumber(extraKpis?.percentToGoal, 0);

  const totalProposedCents = toNumber(yearToDateData?.yearToDateTotalAmountProposed, 0);
  const totalWonCents = toNumber(yearToDateData?.yearToDateTotalSalesWon, 0);
  const closeRate = toNumber(yearToDateData?.yearToDateCloseRate, 0);
  const netMargin = toNumber(yearToDateData?.yearToDateNetMarginCalculated, 0);

  const totalPipelineCents = toNumber((extraKpis as any)?.totalPipeline, 0);
  const weightedPipelineCents = toNumber((extraKpis as any)?.weightedPipeline, 0);
  const weightedForecast30Cents = toNumber((extraKpis as any)?.weightedForecast30Days, 0);
  const weightedForecast60Cents = toNumber((extraKpis as any)?.weightedForecast60Days, 0);

  const pacePercent = toNumber((extraKpis as any)?.pace, 0);
  const paceVarianceCents = toNumber((extraKpis as any)?.paceVarianceCents, NaN);
  const paceIsAhead =
    typeof (extraKpis as any)?.paceIsAhead === "boolean" ? (extraKpis as any)?.paceIsAhead : null;

  const hasPaceVariance = Number.isFinite(paceVarianceCents);

  const pacePrimary: string = extraKpisLoading
    ? "—"
    : hasPaceVariance
      ? `${(paceIsAhead ?? paceVarianceCents >= 0) ? "Ahead" : "Behind"} ${formatCentsToUSD(
          Math.abs(paceVarianceCents)
        )}`
      : `${pacePercent}%`;

  const salesGoalText = extraKpisLoading ? "—" : formatCentsToUSDNoDecimals(organizationSalesGoalCents);

  // IMPORTANT: DefaultStatisticsCard count must be string | number (not JSX)
  const totalWonOverGoalText = `${formatCentsToUSDNoDecimals(totalWonCents)} / ${salesGoalText}`;
  const closeRateOverNetMarginText = `${closeRate}% / ${netMargin}%`;

  return (
    <>
      <MDBox textAlign="center" mb={2}>
        <MDTypography variant="h4">Year to Date</MDTypography>
      </MDBox>
      <MDBox>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4} lg={2.4}>
            <MDBox mb={1.5}>
              <DefaultStatisticsCard
                title="Total Amount Proposed"
                count={formatCentsToUSDNoDecimals(totalProposedCents)}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={4} lg={2.4}>
            <MDBox mb={1.5}>
              <DefaultStatisticsCard title="Total Won / Sales Goal" count={totalWonOverGoalText} />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={4} lg={2.4}>
            <MDBox mb={1.5}>
              <DefaultStatisticsCard title="Close Rate / Net Margin" count={closeRateOverNetMarginText} />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={2.4}>
            <MDBox mb={1.5}>
              <DefaultStatisticsCard
                title="% to Goal"
                count={extraKpisLoading ? "—" : `${percentToGoal}%`}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={2.4}>
            <MDBox mb={1.5}>
              <DefaultStatisticsCard title="Pace to Goal" count={pacePrimary} />
            </MDBox>
          </Grid>
        </Grid>
        <MDBox mt={1}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4} lg={2.4}>
              <MDBox mb={1.5}>
                <DefaultStatisticsCard
                  title="Total Pipeline (Sent Proposals)"
                  count={extraKpisLoading ? "—" : formatCentsToUSDNoDecimals(totalPipelineCents)}
                />
              </MDBox>
            </Grid>
            <Grid item xs={12} md={4} lg={2.4}>
              <MDBox mb={1.5}>
                <DefaultStatisticsCard
                  title="Weighted Pipeline"
                  count={extraKpisLoading ? "—" : formatCentsToUSDNoDecimals(weightedPipelineCents)}
                />
              </MDBox>
            </Grid>
            <Grid item xs={12} md={4} lg={2.4}>
              <MDBox mb={1.5}>
                <DefaultStatisticsCard
                  title="Next 30-Day Forecast"
                  count={extraKpisLoading ? "—" : formatCentsToUSDNoDecimals(weightedForecast30Cents)}
                />
              </MDBox>
            </Grid>
            <Grid item xs={12} md={6} lg={2.4}>
              <MDBox mb={1.5}>
                <DefaultStatisticsCard
                  title="Next 60-Day Forecast"
                  count={extraKpisLoading ? "—" : formatCentsToUSDNoDecimals(weightedForecast60Cents)}
                />
              </MDBox>
            </Grid>
            <Grid item xs={12} md={6} lg={2.4}>
              <MDBox mb={1.5}>
                <DefaultStatisticsCard
                  title="Average Proposal Amount"
                  count={extraKpisLoading ? "—" : formatCentsToUSDNoDecimals(averageProposalAmountCents)}
                />
              </MDBox>
            </Grid>
          </Grid>
        </MDBox>
      </MDBox>
    </>
  );
}
